import {
  BankAccountType,
  BankReferenceBankType,
  BankType,
  ConditionType,
  CurrencyCode,
  CurrencyRatio,
  DepartmentType,
  DiscountType,
  EWalletProviderType,
  ExecutiveSummaryPeriodType,
  ExecutiveSummaryStatus,
  ExpenseType,
  MerchantType,
  OrdinaryPersonPrefixType,
  ProductVatType,
  SortingType,
  TaxIncomeType,
  TaxSourceType,
  TaxStatusType,
  TransactionType,
  WhtAmountType,
} from "@/enums/enums";
import { AddressDetailModel } from "@/model/address/addressModel";
import { ContactModel, DepartmentModel, OrdinaryPersonModel, PersonModel } from "@/model/contact/contactModel";
import {
  ContactEnvelopeDocumentModel,
  ContactPersonEnvelopeModel,
  DocumentContactEnvelopeModel,
  DocumentMerchantEnvelopeModel,
} from "@/model/document/contactEnvelopeModel";
import {
  ClassificationItemElement,
  ClassificationsItemModel,
  CredentialModel,
  CustomizeDocumentDataModel,
  DefaultPaymentMethodChannelModel,
  DepositReferenceItemModel,
  DocumentAccountModel,
  DocumentAssetGroupModel,
  DocumentAssetModel,
  DocumentBillingBaseModel,
  DocumentChequeModel,
  DocumentCreditNoteandDebitNoteSummaryModel,
  DocumentCurrencyModel,
  DocumentDataModel,
  DocumentDiscountModel,
  DocumentItemAssetBaseModel,
  DocumentItemAssetGroupBaseModel,
  DocumentItemBaseModel,
  DocumentPaymentMethodModel,
  DocumentPaymentTransactionModel,
  DocumentPaymentWhtModel,
  DocumentProductImageModel,
  DocumentProductModel,
  DocumentProductUnitModel,
  DocumentPurchaseTaxInvoicesModel,
  DocumentReasonModel,
  DocumentReferenceBilledModel,
  DocumentReferenceCreditNoteModel,
  DocumentReferenceModel,
  DocumentReferenceTypeModel,
  DocumentSummaryModel,
  DocumentWhtModel,
  FromToCurrencyModel,
  MerchantCredentialModel,
  PaymentGroupModel,
  PaymentModel,
  UserCredentialModel,
} from "@/model/document/documentModel";
import {
  DocumentExecutiveSummaryAccountModel,
  DocumentExecutiveSummaryModel,
  DocumentExecutiveSummaryPeriodModel,
  DocumentExecutiveSummaryRemarkModel,
  DocumentExecutiveSummarySettingModel,
  DocumentExecutiveSummaryTransactionItemModel,
  DocumentExecutiveSummaryTransactionModel,
  DocumentExecutiveSummaryValueModel,
  DocumentMerchantExecutiveSummaryModel,
  DocumentUserExecutiveSummaryModel,
  ExecutiveSummaryDocumentModel,
} from "@/model/document/executiveSummaryModel";
import { JournalReferenceModel } from "@/model/document/journalDocumentModal";
import { PP30TransactionModel } from "@/model/document/pp30DocumentModel";
import { WhtCertificateModel, WitholdingTaxItemDocumentModel } from "@/model/document/witholdingTaxDocumentModal";
import { MerchantDetail } from "@/model/merchant/merchantModel";
import { UserInfoModel } from "@/model/user/userModel";
import { ConvertToAddressDetail } from "./addressHelper";
import { CompareDataListForSorting } from "./shareHelper";

export function GetDocumentData(data: any): DocumentDataModel {
  const documentDataModel: DocumentDataModel = {
    acceptedDateTime: data.acceptedDateTime ? data.acceptedDateTime.ToDateTimezone() : "",
    rejectedDateTime: data.rejectedDateTime ? data.rejectedDateTime.ToDateTimezone() : "",
    rejectedUser: data.rejectedUser ? GetDocumentUser(data.rejectedUser) : new UserInfoModel(),
    acceptedUser: data.acceptedUser ? GetDocumentUser(data.acceptedUser) : new UserInfoModel(),
    approvedDateTime: data.approvedDateTime ? data.approvedDateTime.ToDateTimezone() : "",
    approvedUser: data.approvedUser ? GetDocumentUser(data.approvedUser) : new UserInfoModel(),
    contact: data.contact ? GetDocumentContact(data.contact) : new ContactModel(),
    createdDateTime: data.createdDateTime ? data.createdDateTime.ToDateTimezone() : "",
    createdUser: data.createdUser ? GetDocumentUser(data.createdUser) : new UserInfoModel(),
    currency: data.currency ? GetDocumentCurrency(data.currency) : new DocumentCurrencyModel(),
    discount: data.discount ? GetDocumentDiscount(data.discount) : new DocumentDiscountModel(),
    isSendETaxInvoice: data.isSendETaxInvoice ? true : false,
    dueDateTime: data.dueDateTime ? data.dueDateTime.ToDateTimezone() : "",
    isDeleted: data.isDeleted ? data.isDeleted : false,
    isRejected: data.isRejected ? data.isRejected : false,
    issuedDateTime: data.issuedDateTime ? data.issuedDateTime.ToDateTimezone() : "",
    isTaxInvoice: data.isTaxInvoice ? data.isTaxInvoice : false,
    items: data.items ? GetDocumentItemBase(data.items) : new Array<DocumentItemBaseModel>(),
    billing: data.billing ? GetDocumentBillingBase(data.billing) : new Array<DocumentBillingBaseModel>(),
    assets: data.assets ? GetDocumentAssetsBase(data.assets) : new Array<DocumentItemAssetBaseModel>(),
    assetGroups: data.assetGroups
      ? GetDocumentAssetGroupsBase(data.assetGroups)
      : new Array<DocumentItemAssetGroupBaseModel>(),
    merchant: data.merchant ? GetDocumentMerchant(data.merchant) : new MerchantDetail(),
    reference: data.reference ? GetDocumentReference(data.reference) : new DocumentReferenceModel(),
    remark: data.remark ? data.remark : "",
    reason: data.creditNoteReason || data.debitNoteReason ? GetDocumentReason(data) : new DocumentReasonModel(),
    statusType: data.statusType ? data.statusType : 0,
    summary: data.summary ? GetDocumentSummary(data.summary) : new DocumentSummaryModel(),
    summaryDocumentModel:
      data.creditNoteSummaryDocumentModel || data.debitNoteSummaryDocumentModel
        ? GetDocumentCreditNoteandDebitNoteSummary(data)
        : new DocumentCreditNoteandDebitNoteSummaryModel(),
    tags: data.tags ? GetDocumentTags(data.tags) : [],
    taxStatusType: data.taxStatusType ? data.taxStatusType : TaxStatusType.NonTax,
    transactionId: data.transactionId ? data.transactionId : 0,
    transactionNumber: data.transactionNumber ? data.transactionNumber : "",
    updatedDateTime: data.updatedDateTime ? data.updatedDateTime.ToDateTimezone() : "",
    updatedUser: data.updatedUser ? GetDocumentUser(data.updatedUser) : new UserInfoModel(),
    paymentGroups:
      data.paymentGroups && data.paymentGroups.length > 0
        ? GetDocumentPaymentGroup(data.paymentGroups)
        : new Array<PaymentGroupModel>(),
    purchaseTaxInvoices: data.purchaseTaxInvoices
      ? GetDocumentPurchaseTaxInvoicesModel(data.purchaseTaxInvoices)
      : new DocumentPurchaseTaxInvoicesModel(),
    deposit: data.deposit
      ? GetDocumentDepositReferenceItemModelsModel(data.deposit)
      : new Array<DepositReferenceItemModel>(),
    classifications: data.classifications
      ? GetClassificationsItemsModel(data.classifications)
      : new Array<ClassificationsItemModel>(),
  };

  return documentDataModel;
}

export function GetCustomizeData(data: any): CustomizeDocumentDataModel {
  const documentDataModel: CustomizeDocumentDataModel = {
    css: data.templateCss,
    html: data.templateHtml,
    id: data.customizeId,
  };

  return documentDataModel;
}

export function GetContactEnvelopeDocumentData(data: any): ContactEnvelopeDocumentModel {
  const documentDataModel: ContactEnvelopeDocumentModel = {
    createdDateTime: data.createdDateTime ? data.createdDateTime.ToDateTimezone() : "",
    createdUser: data.createdUser ? GetDocumentUser(data.createdUser) : new UserInfoModel(),
    contact: data.contact ? GetContactEnvelopeDocument(data.contact) : new DocumentContactEnvelopeModel(),
    merchant: data.merchant ? GetMerchantEnvelopeDocument(data.merchant) : new DocumentMerchantEnvelopeModel(),
  };

  return documentDataModel;
}

export function GetDocumentPurchaseTaxInvoicesModel(data: any): DocumentPurchaseTaxInvoicesModel {
  const purchaseTaxInvoicesModel: DocumentPurchaseTaxInvoicesModel = {
    taxInvoiceDateTime: data[0].taxInvoiceDateTime ? data[0].taxInvoiceDateTime.ToDateTimezone() : "",
    taxInvoiceNumber: data[0].taxInvoiceNumber ? data[0].taxInvoiceNumber : "",
  };
  return purchaseTaxInvoicesModel;
}

export function GetDefaultPaymentMethodChannel(data: any) {
  let defaultPaymentMethodChannel: DefaultPaymentMethodChannelModel = {
    channelList: new Array<DocumentPaymentMethodModel>(),
  };

  if (data.channelList && data.channelList.length > 0) {
    data.channelList.forEach((channel: any) => {
      let paymentMethod = GetPaymentMethod(channel);
      defaultPaymentMethodChannel.channelList.push(paymentMethod);
    });
  }

  return defaultPaymentMethodChannel;
}

export function GetDocumentPaymentGroup(data: any) {
  const documentPaymentGroups = new Array<PaymentGroupModel>();
  for (let documentPaymentGroup of data) {
    const paymentGroup: PaymentGroupModel = {
      paymentDateTime: documentPaymentGroup.paymentDateTime
        ? documentPaymentGroup.paymentDateTime.ToDateTimezone()
        : new Date(),
      paymentTotal: documentPaymentGroup.paymentTotal ? documentPaymentGroup.paymentTotal : 0,
      wht: documentPaymentGroup.wht ? GetDocumentWht(documentPaymentGroup.wht) : new DocumentPaymentWhtModel(),
      whtTotal: documentPaymentGroup.whtTotal ? documentPaymentGroup.whtTotal : 0,
      payments: documentPaymentGroup.payments ? GetPayment(documentPaymentGroup.payments) : new Array<PaymentModel>(),
    };
    documentPaymentGroups.push(paymentGroup);
  }

  return documentPaymentGroups;
}

export function GetPayment(data: any) {
  const documentPayments = new Array<PaymentModel>();
  for (let documentPayment of data) {
    documentPayments.push({
      amount: documentPayment.amount ? documentPayment.amount : 0,
      remark: documentPayment.remark ? documentPayment.remark : "",
      paymentAccount: documentPayment.paymentAccount
        ? GetDocumentAccount(documentPayment.paymentAccount)
        : new DocumentAccountModel(),
      paymentMethod: documentPayment.paymentMethod
        ? GetPaymentMethod(documentPayment.paymentMethod)
        : new DocumentPaymentMethodModel(),
      paymentCheque: documentPayment.paymentCheque
        ? GetPaymentCheque(documentPayment.paymentCheque)
        : new DocumentChequeModel(),
      paymentTransaction: documentPayment.paymentTransaction
        ? GetPaymentTransacton(documentPayment.paymentTransaction)
        : new DocumentPaymentTransactionModel(),
    });
  }
  return documentPayments;
}

export function GetPaymentTransacton(data: any) {
  const paymentTransaction: DocumentPaymentTransactionModel = {
    transactionId: data.transactionId ? data.transactionId : 0,
    transactionType: data.transactionType ? data.transactionType : TransactionType.None,
    transactionItemType: data.transactionItemType ? data.transactionItemType : ExpenseType.None,
    transactionNumber: data.transactionNumber ? data.transactionNumber : "",
  };
  return paymentTransaction;
}

export function GetPaymentCheque(data: any) {
  const paymentCheque: DocumentChequeModel = {
    chequeBankReferenceId: data.chequeBankReferenceId ? data.chequeBankReferenceId : 0,
    chequeCode: data.chequeCode ? data.chequeCode : "",
    chequeDate: data.chequeDate ? data.chequeDate.ToDateTimezone() : new Date(),
    chequeId: data.chequeId ? data.chequeId : 0,
    chequeNumber: data.chequeNumber ? data.chequeNumber : "",
  };
  return paymentCheque;
}

export function GetPaymentMethod(data: any) {
  if (data) {
    const paymentMethod: DocumentPaymentMethodModel = {
      paymentMethodCode: data.paymentMethodCode ? data.paymentMethodCode : "",
      paymentMethodId: data.paymentMethodId ? data.paymentMethodId : 0,
      paymentMethodName: data.paymentMethodName ? data.paymentMethodName : "",
      paymentMethodType: data.paymentMethodType !== null ? data.paymentMethodType : BankAccountType.Bank,
      bankAccountNumber: data.bankAccountNumber ? data.bankAccountNumber : "",
      bankAccountType: data.bankAccountType ? data.bankAccountType : BankType.None,
      bankReferenceType: data.bankReferenceType ? data.bankReferenceType : BankReferenceBankType.OTHER,
      eWalletProviderType: data.eWalletProviderType ? data.eWalletProviderType : EWalletProviderType.None,
      eWalletNumber: data.eWalletNumber ? data.eWalletNumber : "",
      bankAccountBranchCode: data.bankAccountBranchCode ? data.bankAccountBranchCode : "",
      bankAccountBranchName: data.bankAccountBranchName ? data.bankAccountBranchName : "",
    };
    return paymentMethod;
  }
  return new DocumentPaymentMethodModel();
}

export function GetDocumentTags(data: any) {
  const documentTags = [];
  for (let tag of data) {
    documentTags.push(tag);
  }
  return documentTags;
}
export function GetDocumentContact(data: any): ContactModel {
  const documentContactModel: ContactModel = {
    branchCode: data.branchCode ? data.branchCode : "",
    contactCode: data.contactCode ? data.contactCode : "",
    contactId: data.contactId ? data.contactId : 0,
    email: data.email ? data.email : "",
    faxNumber: data.faxNumber ? data.faxNumber : "",
    isOrdinaryPerson: data.isOrdinaryPerson ? data.isOrdinaryPerson : false,
    merchantType: data.merchantType ? data.merchantType : MerchantType.None,
    name: data.name ? data.name : "",
    ordinaryPerson: data.ordinaryPerson ? GetDocumentOrdinaryPerson(data.ordinaryPerson) : new OrdinaryPersonModel(),
    person: data.person ? GetDocumentPerson(data.person) : new PersonModel(),
    phoneNumber: data.phoneNumber ? data.phoneNumber : "",
    address: data.address ? data.address : "",
    road: data.road ? data.road : "",
    building: data.building ? data.building : "",
    soi: data.soi ? data.soi : "",
    floorNo: data.floorNo ? data.floorNo : "",
    roomNo: data.roomNo ? data.roomNo : "",
    homeNo: data.homeNo ? data.homeNo : "",
    homeGroup: data.homeGroup ? data.homeGroup : "",
    district: data.district ? data.district : "",
    subDistrict: data.subDistrict ? data.subDistrict : "",
    postCode: data.postCode ? data.postCode : "",
    province: data.province ? data.province : "",
    country: data.country ? data.country : "",
    taxNumber: data.taxNumber ? data.taxNumber : "",
    website: data.website ? data.website : "",
    taxId: data.taxId ? data.taxId : "",
  };
  return documentContactModel;
}
export function GetContactEnvelopeDocument(data: DocumentContactEnvelopeModel): DocumentContactEnvelopeModel {
  const documentContactModel: DocumentContactEnvelopeModel = {
    contactId: data.contactId,
    contactCode: data.contactCode,
    name: data.name,
    merchantType: data.merchantType,
    taxNumber: data.taxNumber,
    branchCode: data.branchCode,
    phoneNumber: data.phoneNumber,
    faxNumber: data.faxNumber,
    email: data.email,
    website: data.website,
    isOrdinaryPerson: data.isOrdinaryPerson,
    ordinaryPerson: data.ordinaryPerson ? GetDocumentOrdinaryPerson(data.ordinaryPerson) : new OrdinaryPersonModel(),
    persons: data.persons ? GetDocumentPersonList(data.persons) : new Array<ContactPersonEnvelopeModel>(),
    registerAddress: data.registerAddress ? ConvertToAddressDetail(data.registerAddress) : new AddressDetailModel(),
    shippingAddress: data.shippingAddress ? ConvertToAddressDetail(data.shippingAddress) : new AddressDetailModel(),
  };
  return documentContactModel;
}
export function GetMerchantEnvelopeDocument(data: any): DocumentMerchantEnvelopeModel {
  const documentContactModel: DocumentMerchantEnvelopeModel = {
    merchantId: data.merchantId ? data.merchantId : 0,
    isVat: data.isVat ? data.isVat : false,
    line: data.line ? data.line : "-",
    logoUrl: data.logoUrl ? process.env.VUE_APP_PeakBlobStorage.UrlCombine("peakengine", data.logoUrl) : "",
    name: data.name ? data.name : "-",
    merchantType: data.merchantType ? data.merchantType : MerchantType.None,
    taxNumber: data.taxNumber ? data.taxNumber : "-",
    branchCode: data.branchCode ? data.branchCode : "-",
    phoneNumber: data.phoneNumber ? data.phoneNumber : "-",
    faxNumber: data.faxNumber ? data.faxNumber : "-",
    email: data.email ? data.email : "-",
    website: data.website ? data.website : "-",
    user: data.user ? GetDocumentUser(data.user) : new UserInfoModel(),
    registerAddress: data.registerAddress ? ConvertToAddressDetail(data.registerAddress) : new AddressDetailModel(),
    shippingAddress: data.shippingAddress ? ConvertToAddressDetail(data.shippingAddress) : new AddressDetailModel(),
  };
  return documentContactModel;
}
export function GetDocumentOrdinaryPerson(data: any): OrdinaryPersonModel {
  const documentOrdinaryPersonModel: OrdinaryPersonModel = {
    namePrefixOther: data.namePrefixOther ? data.namePrefixOther : "",
    namePrefixType: data.namePrefixType ? data.namePrefixType : OrdinaryPersonPrefixType.None,
  };
  return documentOrdinaryPersonModel;
}
export function GetDocumentPerson(data: any): PersonModel {
  const documentPersonModel: PersonModel = {
    contactPersonId: data.contactPersonId ? data.contactPersonId : 0,
    email: data.email ? data.email : "-",
    firstName: data.firstName ? data.firstName : "",
    lastName: data.lastName ? data.lastName : "",
    ordinaryPerson: data.ordinaryPerson ? GetDocumentOrdinaryPerson(data.ordinaryPerson) : new OrdinaryPersonModel(),
    phoneNumber: data.phoneNumber ? data.phoneNumber : "-",
    department: data.department ? data.department : new DepartmentModel(),
  };
  return documentPersonModel;
}
export function GetDocumentPersonList(data: Array<any>): Array<ContactPersonEnvelopeModel> {
  const documentPersonModel = new Array<ContactPersonEnvelopeModel>();

  data.forEach((item, index) => {
    documentPersonModel.push({
      contactPersonId: item.contactPersonId ? item.contactPersonId : 0,
      email: item.email ? item.email : "",
      fullName: "",
      firstName: item.firstName ? item.firstName : "",
      lastName: item.lastName ? item.lastName : "",
      ordinaryPerson: item.ordinaryPerson ? GetDocumentOrdinaryPerson(item.ordinaryPerson) : new OrdinaryPersonModel(),
      phoneNumber: item.phoneNumber ? item.phoneNumber : "",
      department: item.department ? item.department : new DepartmentModel(),
      isArchived: item.isArchived ? item.isArchived : false,
      isMainPerson: item.isMainPerson ? item.isMainPerson : false,
    });
  });
  return documentPersonModel;
}
export function GetContactEnvelopetPersonModelDefault() {
  const personDetail: ContactPersonEnvelopeModel = {
    contactPersonId: 0,
    email: "",
    fullName: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    isArchived: false,
    isMainPerson: false,
    ordinaryPerson: {
      namePrefixOther: "",
      namePrefixType: OrdinaryPersonPrefixType.None,
    },
    department: {
      departmentOther: "",
      departmentType: DepartmentType.None,
    },
  };
  return personDetail;
}
export function GetDocumentCurrency(data: any): DocumentCurrencyModel {
  const documentCurrencyModel: DocumentCurrencyModel = {
    exchangeRates: data.exchangeRates ? data.exchangeRates : 0,
    exchangeRatioType: data.exchangeRatioType ? data.exchangeRatioType : CurrencyRatio.None,
    fromCurrency: data.fromCurrency ? GetDocumentFromToCurrency(data.fromCurrency) : new FromToCurrencyModel(),
    netAmount: data.netAmount ? data.netAmount : 0,
    preVatTotal: data.preVatTotal ? data.preVatTotal : 0,
    subTotal: data.subTotal ? data.subTotal : 0,
    toCurrency: data.toCurrency ? GetDocumentFromToCurrency(data.toCurrency) : new FromToCurrencyModel(),
    vatExemptedTotal: data.vatExemptedTotal ? data.vatExemptedTotal : 0,
    vatTotal: data.vatTotal ? data.vatTotal : 0,
    whtTotal: data.whtTotal ? data.whtTotal : 0,
    zeroVatTotal: data.zeroVatTotal ? data.zeroVatTotal : 0,
  };
  return documentCurrencyModel;
}
export function GetDocumentFromToCurrency(data: any): FromToCurrencyModel {
  const fromToCurrencyModel: FromToCurrencyModel = {
    currencyCode: data.currencyCode ? data.currencyCode : CurrencyCode.THB,
  };
  return fromToCurrencyModel;
}
export function GetDocumentDiscount(data: any): DocumentDiscountModel {
  const documentDiscountModel: DocumentDiscountModel = {
    discountAmount: data.discountAmount ? data.discountAmount : 0,
    discountType: data.discountType ? data.discountType : DiscountType.Amount,
  };
  return documentDiscountModel;
}
export function GetDocumentReference(data: any): DocumentReferenceModel {
  const documentReferenceModel: DocumentReferenceModel = {
    text: data.text ? data.text : "",
    document: data.document ? DocumentReferenceType(data.document) : new DocumentReferenceTypeModel(),
  };
  return documentReferenceModel;
}
export function DocumentReferenceType(data: any): DocumentReferenceTypeModel {
  const documentReferenceTypeModel: DocumentReferenceTypeModel = {
    transactionId: data.transactionId ? data.transactionId : 0,
    transactionType: data.transactionType ? data.transactionType : TransactionType.None,
    transactionNumber: data.transactionNumber ? data.transactionNumber : "",
  };
  return documentReferenceTypeModel;
}
export function GetDocumentSummary(data: any): DocumentSummaryModel {
  const documentSummaryModel: DocumentSummaryModel = {
    netAmount: data.netAmount ? data.netAmount : 0,
    preVatTotal: data.preVatTotal ? data.preVatTotal : 0,
    subTotal: data.subTotal ? data.subTotal : 0,
    vatExemptedTotal: data.vatExemptedTotal ? data.vatExemptedTotal : 0,
    vatTotal: data.vatTotal ? data.vatTotal : 0,
    whtTotal: data.whtTotal ? data.whtTotal : 0,
    zeroVatTotal: data.zeroVatTotal ? data.zeroVatTotal : 0,
  };
  return documentSummaryModel;
}

export function CalCulateTotalDocumentAmountAfterCreditNoteandDebitNote(
  referenceTransactionCurrencyAmount: any,
  summaryModel: any,
  isCreditNote: any
) {
  if (isCreditNote) {
    return referenceTransactionCurrencyAmount
      ? referenceTransactionCurrencyAmount -
          (summaryModel.vatExemptedTotal + summaryModel.preVatTotal + summaryModel.zeroVatTotal)
      : 0;
  } else {
    return referenceTransactionCurrencyAmount
      ? referenceTransactionCurrencyAmount +
          (summaryModel.vatExemptedTotal + summaryModel.preVatTotal + summaryModel.zeroVatTotal)
      : 0;
  }
}

export function GetDocumentCreditNoteandDebitNoteCurrencySummary(
  referenceTransactionCurrencyAmount: any,
  summaryModel: any,
  isCreditNote: any = false
) {
  let referenceTransactionAmount = 0;
  let totalDocumentAmountAfter = 0;
  referenceTransactionAmount = referenceTransactionCurrencyAmount ? referenceTransactionCurrencyAmount : 0;
  totalDocumentAmountAfter = CalCulateTotalDocumentAmountAfterCreditNoteandDebitNote(
    referenceTransactionCurrencyAmount,
    summaryModel,
    isCreditNote
  );
  const documentCreditNoteandDebitNoteummaryModel: DocumentCreditNoteandDebitNoteSummaryModel = {
    referenceTransactionAmount: referenceTransactionAmount,
    totalDocumentAmountAfter: totalDocumentAmountAfter,
  };
  return documentCreditNoteandDebitNoteummaryModel;
}

export function GetDocumentCreditNoteandDebitNoteSummary(data: any): DocumentCreditNoteandDebitNoteSummaryModel {
  let documentCreditNoteandDebitNoteummaryModel: DocumentCreditNoteandDebitNoteSummaryModel = {
    referenceTransactionAmount: 0,
    totalDocumentAmountAfter: 0,
  };
  const isNotThaiCurrency = data.currency.toCurrency.currencyCode > 0;
  let isCreditNote = false;
  if (data.creditNoteSummaryDocumentModel) {
    const referenceTransactionAmount = isNotThaiCurrency
      ? data.creditNoteSummaryDocumentModel.referenceTransactionCurrencyAmount
      : data.creditNoteSummaryDocumentModel.referenceTransactionAmount;

    const summaryModel = isNotThaiCurrency ? data.currency : data.summary;
    isCreditNote = true;
    documentCreditNoteandDebitNoteummaryModel = GetDocumentCreditNoteandDebitNoteCurrencySummary(
      referenceTransactionAmount,
      summaryModel,
      isCreditNote
    );
  } else if (data.debitNoteSummaryDocumentModel) {
    const referenceTransactionAmount = isNotThaiCurrency
      ? data.debitNoteSummaryDocumentModel.referenceTransactionCurrencyAmount
      : data.debitNoteSummaryDocumentModel.referenceTransactionAmount;

    const summaryModel = isNotThaiCurrency ? data.currency : data.summary;

    documentCreditNoteandDebitNoteummaryModel = GetDocumentCreditNoteandDebitNoteCurrencySummary(
      referenceTransactionAmount,
      summaryModel
    );
  }

  return documentCreditNoteandDebitNoteummaryModel;
}

export function GetJournalReference(data: any): JournalReferenceModel {
  let journalReference: JournalReferenceModel = {
    referenceId: data.referenceId,
    referenceNumber: data.referenceNumber,
    referenceType: data.referenceType,
  };

  return journalReference;
}
export function GetDocumentMerchant(data: any): MerchantDetail {
  const documentMerchantModel: MerchantDetail = {
    branchCode: data.branchCode ? data.branchCode : "",
    email: data.email ? data.email : "",
    faxNumber: data.faxNumber ? data.faxNumber : "",
    isVat: data.isVat ? data.isVat : false,
    line: data.line ? data.line : "",
    merchantId: data.merchantId ? data.merchantId : 0,
    merchantType: data.merchantType ? data.merchantType : MerchantType.None,
    name: data.name ? data.name : "",
    phoneNumber: data.phoneNumber ? data.phoneNumber : "",
    address: data.address ? data.address : "",
    road: data.road ? data.road : "",
    building: data.building ? data.building : "",
    soi: data.soi ? data.soi : "",
    floorNo: data.floorNo ? data.floorNo : "",
    roomNo: data.roomNo ? data.roomNo : "",
    homeNo: data.homeNo ? data.homeNo : "",
    homeGroup: data.homeGroup ? data.homeGroup : "",
    district: data.district ? data.district : "",
    subDistrict: data.subDistrict ? data.subDistrict : "",
    postCode: data.postCode ? data.postCode : "",
    province: data.province ? data.province : "",
    country: data.country ? data.country : "",
    taxNumber: data.taxNumber ? data.taxNumber : "",
    user: data.user ? GetDocumentUser(data.user) : new UserInfoModel(),
    website: data.website ? data.website : "",
    logoUrl: data.logoUrl ? process.env.VUE_APP_PeakBlobStorage.UrlCombine("peakengine", data.logoUrl) : "",
    stampUrl: data.stampUrl ? process.env.VUE_APP_PeakBlobStorage.UrlCombine("peakengine", data.stampUrl) : "",
  };
  return documentMerchantModel;
}
export function GetDocumentUser(data: any): UserInfoModel {
  const documentUserModel: UserInfoModel = {
    email: data.email ? data.email : "",
    firstName: data.firstName ? data.firstName : "",
    lastName: data.lastName ? data.lastName : "",
    phoneNumber: data.phoneNumber ? data.phoneNumber : "",
    signatureUrl: data.signatureUrl
      ? process.env.VUE_APP_PeakBlobStorage.UrlCombine("peakengine", data.signatureUrl)
      : "",
    userId: data.userId ? data.userId : 0,
  };
  return documentUserModel;
}
export function GetDocumentBillingBase(data: any): Array<DocumentBillingBaseModel> {
  const documentBillingBaseModel = new Array<DocumentBillingBaseModel>();
  let index = 0;

  for (let documentBillingBase of data) {
    documentBillingBaseModel.push({
      index: index,
      billedAmount: documentBillingBase.billedAmount ? documentBillingBase.billedAmount : 0,
      billedCurrencyAmount: documentBillingBase.billedCurrencyAmount ? documentBillingBase.billedCurrencyAmount : 0,
      isDescriptionOnly: documentBillingBase.isDescriptionOnly,
      description: documentBillingBase.description ? documentBillingBase.description : "",
      itemId: documentBillingBase.itemId ? documentBillingBase.itemId : 0,
      sequenceNumber: documentBillingBase.sequenceNumber ? documentBillingBase.sequenceNumber : 0,
      document: documentBillingBase.document
        ? GetDocumentReferenceBilled(documentBillingBase.document)
        : new DocumentReferenceBilledModel(),
      wht: documentBillingBase.wht ? GetDocumentWht(documentBillingBase.wht) : new DocumentWhtModel(),
      items: GetItemsDocument(documentBillingBase),
      purchaseTaxInvoices: documentBillingBase.purchaseTaxInvoices ? documentBillingBase.purchaseTaxInvoices : [],
    });
    index++;
  }
  return documentBillingBaseModel;
}

export function GetItemsDocument(documentBillingBase: any) {
  if (documentBillingBase.items && documentBillingBase.items.length > 0) {
    return GetDocumentItemBase(documentBillingBase.items);
  } else if (documentBillingBase.assets && documentBillingBase.assets.length > 0) {
    return GetDocumentItemBase(documentBillingBase.assets);
  } else {
    return [];
  }
}

export function GetDocumentAsset(asset: any) {
  const documentAssetModel: DocumentAssetModel = {
    assetCode: asset.assetCode ? asset.assetCode : "",
    assetId: asset.id ? asset.id : 0,
  };
  return documentAssetModel;
}

export function GetDocumentAssetGroup(assetGroup: any) {
  const documentAssetGroupModel: DocumentAssetGroupModel = {
    assetGroupCode: assetGroup.assetGroupCode ? assetGroup.assetGroupCode : "",
    assetGroupId: assetGroup.assetGroupId ? assetGroup.assetGroupId : 0,
    name: assetGroup.name ? assetGroup.name : "",
    nameEn: assetGroup.nameEn ? assetGroup.nameEn : "",
  };
  return documentAssetGroupModel;
}

export function GetDocumentAssetsBase(data: any): Array<DocumentItemAssetBaseModel> {
  const documentItemAssetBaseModel = new Array<DocumentItemAssetBaseModel>();
  let index = 0;

  for (let documentItemAssetBase of data) {
    documentItemAssetBaseModel.push({
      index: index,
      itemId: documentItemAssetBase.itemId ? documentItemAssetBase.itemId : 0,
      isDescriptionOnly: documentItemAssetBase.isDescriptionOnly ? documentItemAssetBase.isDescriptionOnly : false,
      sequenceNumber: documentItemAssetBase.sequenceNumber ? documentItemAssetBase.sequenceNumber : 0,
      description: documentItemAssetBase.description ? documentItemAssetBase.description : "",
      vatAmount: documentItemAssetBase.vatAmount ? documentItemAssetBase.vatAmount : 0,
      netAmount: documentItemAssetBase.netAmount ? documentItemAssetBase.netAmount : 0,
      subTotal: documentItemAssetBase.subTotal ? documentItemAssetBase.subTotal : 0,
      quantity: documentItemAssetBase.quantity ? documentItemAssetBase.quantity : 0,
      price: documentItemAssetBase.price ? documentItemAssetBase.price : 0,
      incVat: GetIncVat(documentItemAssetBase.subTotal, documentItemAssetBase.vatAmount),
      unit: {
        name: documentItemAssetBase.unit ? documentItemAssetBase.unit.name : "",
        nameEn: documentItemAssetBase.unit ? documentItemAssetBase.unit.nameEn : "",
        unitId: documentItemAssetBase.unit ? documentItemAssetBase.unit.unitId : 0,
      },
      vatType: documentItemAssetBase.vatType ? documentItemAssetBase.vatType : ProductVatType.None,
      wht: documentItemAssetBase.wht ? GetDocumentWht(documentItemAssetBase.wht) : new DocumentWhtModel(),
      discount: documentItemAssetBase.discount
        ? GetDocumentDiscount(documentItemAssetBase.discount)
        : new DocumentDiscountModel(),

      assetGroup: documentItemAssetBase.assetGroup
        ? GetDocumentAssetGroup(documentItemAssetBase.assetGroup)
        : new DocumentAssetGroupModel(),
      asset: documentItemAssetBase.asset ? GetDocumentAsset(documentItemAssetBase.asset) : new DocumentAssetModel(),
    });
    index++;
  }
  return documentItemAssetBaseModel;
}

export function GetDocumentAssetGroupsBase(data: any): Array<DocumentItemAssetGroupBaseModel> {
  const documentItemAssetGroupBaseModel = new Array<DocumentItemAssetGroupBaseModel>();
  let index = 0;

  for (let documentItemAssetGroupBase of data) {
    documentItemAssetGroupBaseModel.push({
      index: index,
      itemId: documentItemAssetGroupBase.itemId ? documentItemAssetGroupBase.itemId : 0,
      isDescriptionOnly: documentItemAssetGroupBase.isDescriptionOnly
        ? documentItemAssetGroupBase.isDescriptionOnly
        : false,
      sequenceNumber: documentItemAssetGroupBase.sequenceNumber ? documentItemAssetGroupBase.sequenceNumber : 0,
      description: documentItemAssetGroupBase.description ? documentItemAssetGroupBase.description : "",
      vatAmount: documentItemAssetGroupBase.vatAmount ? documentItemAssetGroupBase.vatAmount : 0,
      netAmount: documentItemAssetGroupBase.netAmount ? documentItemAssetGroupBase.netAmount : 0,
      subTotal: documentItemAssetGroupBase.subTotal ? documentItemAssetGroupBase.subTotal : 0,
      quantity: documentItemAssetGroupBase.quantity ? documentItemAssetGroupBase.quantity : 0,
      price: documentItemAssetGroupBase.price ? documentItemAssetGroupBase.price : 0,
      incVat: GetIncVat(documentItemAssetGroupBase.subTotal, documentItemAssetGroupBase.vatAmount),
      unit: {
        name: documentItemAssetGroupBase.unit ? documentItemAssetGroupBase.unit.name : "",
        nameEn: documentItemAssetGroupBase.unit ? documentItemAssetGroupBase.unit.nameEn : "",
        unitId: documentItemAssetGroupBase.unit ? documentItemAssetGroupBase.unit.unitId : 0,
      },
      vatType: documentItemAssetGroupBase.vatType ? documentItemAssetGroupBase.vatType : ProductVatType.None,
      wht: documentItemAssetGroupBase.wht ? GetDocumentWht(documentItemAssetGroupBase.wht) : new DocumentWhtModel(),
      discount: documentItemAssetGroupBase.discount
        ? GetDocumentDiscount(documentItemAssetGroupBase.discount)
        : new DocumentDiscountModel(),

      assetGroup: documentItemAssetGroupBase.assetGroup
        ? GetDocumentAssetGroup(documentItemAssetGroupBase.assetGroup)
        : new DocumentAssetGroupModel(),
    });
    index++;
  }
  return documentItemAssetGroupBaseModel;
}

export function GetDocumentReferenceBilled(data: any) {
  const documentReferenceBilled: DocumentReferenceBilledModel = {
    dueDateTime: data.dueDateTime ? data.dueDateTime.ToDateTimezone() : new Date(),
    issuedDateTime: data.issuedDateTime ? data.issuedDateTime.ToDateTimezone() : new Date(),
    transactionId: data.transactionId ? data.transactionId : 0,
    transactionNetAmount: data.transactionNetAmount ? data.transactionNetAmount : 0,
    transactionCurrencyNetAmount: data.transactionCurrencyNetAmount ? data.transactionCurrencyNetAmount : 0,
    transactionNumber: data.transactionNumber ? data.transactionNumber : "",
    transactionType: data.transactionType ? data.transactionType : TransactionType.None,
    creditNote: data.creditNote
      ? GetDocumentReferenceCreditNote(data.creditNote)
      : Array<DocumentReferenceCreditNoteModel>(),
    exchangeRates: data.exchangeRates,
    exchangeRatioType: data.exchangeRatioType,
  };

  return documentReferenceBilled;
}

export function GetDocumentReferenceCreditNote(
  data: Array<DocumentReferenceCreditNoteModel>
): Array<DocumentReferenceCreditNoteModel> {
  const documentReferenceCreditNote = new Array<DocumentReferenceCreditNoteModel>();
  data.forEach((creditNote: DocumentReferenceCreditNoteModel) => {
    documentReferenceCreditNote.push({
      transactionId: creditNote.transactionId,
      transactionNetAmount: creditNote.transactionNetAmount,
      transactionNumber: creditNote.transactionNumber,
      transactionType: creditNote.transactionType,
      dueDateTime: creditNote.dueDateTime,
      exchangeRates: creditNote.exchangeRates,
      exchangeRatioType: creditNote.exchangeRatioType,
      issuedDateTime: creditNote.issuedDateTime,
      transactionCurrencyNetAmount: creditNote.transactionCurrencyNetAmount,
      fromCurrency: creditNote.fromCurrency,
      toCurrency: creditNote.toCurrency,
    });
  });
  return documentReferenceCreditNote;
}

export function GetDocumentItemBase(data: any): Array<DocumentItemBaseModel> {
  const documentItemBaseModel = new Array<DocumentItemBaseModel>();
  let index = 0;

  for (let documentItemBase of data) {
    documentItemBaseModel.push({
      index: index,
      itemId: documentItemBase.itemId ? documentItemBase.itemId : 0,
      isDescriptionOnly: documentItemBase.isDescriptionOnly ? documentItemBase.isDescriptionOnly : false,
      sequenceNumber: documentItemBase.sequenceNumber ? documentItemBase.sequenceNumber : 0,
      description: documentItemBase.description ? documentItemBase.description : "",
      vatAmount: documentItemBase.vatAmount ? documentItemBase.vatAmount : 0,
      netAmount: documentItemBase.netAmount ? documentItemBase.netAmount : 0,
      subTotal: documentItemBase.subTotal ? documentItemBase.subTotal : 0,
      incVat: GetIncVat(documentItemBase.subTotal, documentItemBase.vatAmount),
      account: documentItemBase.account ? GetDocumentAccount(documentItemBase.account) : new DocumentAccountModel(),
      quantity: documentItemBase.quantity ? documentItemBase.quantity : 0,
      price: documentItemBase.price ? documentItemBase.price : 0,
      vatType: documentItemBase.vatType ? documentItemBase.vatType : ProductVatType.None,
      wht: documentItemBase.wht ? GetDocumentWht(documentItemBase.wht) : new DocumentWhtModel(),
      discount: documentItemBase.discount
        ? GetDocumentDiscount(documentItemBase.discount)
        : new DocumentDiscountModel(),

      product: documentItemBase.product
        ? GetDocumentProduct(documentItemBase.product)
        : documentItemBase.assetGroup
        ? GetConvertDocumentAssetToProduct(documentItemBase.assetGroup)
        : new DocumentProductModel(),
      unit: documentItemBase.unit ? GetDocumentProductUnit(documentItemBase.unit) : new DocumentProductUnitModel(),
    });
    index++;
  }
  return documentItemBaseModel;
}

export function GetDocumentAccount(data: any): DocumentAccountModel {
  const documentAccountModel: DocumentAccountModel = {
    accountId: data.accountId ? data.accountId : 0,
    accountCode: data.accountCode ? data.accountCode : "",
    name: data.name ? data.name : "",
    nameEn: data.nameEn ? data.nameEn : "",
  };
  return documentAccountModel;
}
export function GetDocumentReason(data: any): DocumentReasonModel {
  let documentReasonModel: DocumentReasonModel = { reasonDescription: "", reasonId: 0 };
  if (data.debitNoteReason) {
    documentReasonModel = {
      reasonDescription: data.debitNoteReason.debitNoteDescription ? data.debitNoteReason.debitNoteDescription : "",
      reasonId: data.debitNoteReason.debitNoteReasonId ? data.debitNoteReason.debitNoteReasonId : 0,
    };
  } else if (data.creditNoteReason) {
    documentReasonModel = {
      reasonDescription: data.creditNoteReason.creditNoteDescription ? data.creditNoteReason.creditNoteDescription : "",
      reasonId: data.creditNoteReason.creditNoteReasonId ? data.creditNoteReason.creditNoteReasonId : 0,
    };
  }
  return documentReasonModel;
}

export function GetDocumentProduct(data: any): DocumentProductModel {
  const documentProductModel: DocumentProductModel = {
    productId: data.productId ? data.productId : 0,
    productCode: data.productCode ? data.productCode : "",
    name: data.name ? data.name : "",
    productImages: data.productImages
      ? GetDocumentProductImage(data.productImages)
      : new Array<DocumentProductImageModel>(),
  };
  return documentProductModel;
}
export function GetConvertDocumentAssetToProduct(data: any): DocumentProductModel {
  const documentProductModel: DocumentProductModel = {
    productId: data.assetGroupId ? data.assetGroupId : 0,
    productCode: data.assetGroupCode ? data.assetGroupCode : "",
    name: data.name ? data.name : "",
    productImages: new Array<DocumentProductImageModel>(),
  };
  return documentProductModel;
}
export function GetDocumentProductUnit(data: any): DocumentProductUnitModel {
  const documentProductUnitModel: DocumentProductUnitModel = {
    unitId: data.unitId ? data.unitId : 0,
    name: data.name ? data.name : "",
    nameEn: data.nameEn ? data.nameEn : "",
  };
  return documentProductUnitModel;
}
export function GetIncVat(_subTotal: any, _vatAmount: any) {
  let subTotal = _subTotal ? _subTotal.ToNumber() : 0;
  let vatAmount = _subTotal ? _vatAmount.ToNumber() : 0;
  return subTotal + vatAmount;
}
export function GetDocumentWht(data: any): DocumentPaymentWhtModel {
  const documentWhtModel: DocumentPaymentWhtModel = {
    whtAmount: data.whtAmount ? data.whtAmount : 0,
    whtAmountType:
      data.whtAmountType !== null || data.whtAmountType !== undefined ? data.whtAmountType : WhtAmountType.None,
    whtNumber: data.whtNumber ? data.whtNumber : "",
  };
  return documentWhtModel;
}
export function GetCredential(data: any): CredentialModel {
  let credentialModel: CredentialModel = {
    merchantCredential: new MerchantCredentialModel(),
    userCredential: new Array<UserCredentialModel>(),
    passCode: "",
  };
  if (data) {
    credentialModel = {
      merchantCredential: data.merchantCredential
        ? GetMerchantCredential(data.merchantCredential)
        : new MerchantCredentialModel(),
      userCredential: data.userCredential ? GetUserCredential(data.userCredential) : Array<UserCredentialModel>(),
      passCode: data.passCode ? data.passCode : "",
    };
  }
  return credentialModel;
}
export function GetMerchantCredential(data: any): MerchantCredentialModel {
  const merchantCredentialModel: MerchantCredentialModel = {
    stampUrl: data.stampUrl ? process.env.VUE_APP_PeakBlobStorage.UrlCombine("peakengine", data.stampUrl) : "",
  };
  return merchantCredentialModel;
}
export function GetUserCredential(data: any): Array<UserCredentialModel> {
  const userCredentialModel = new Array<UserCredentialModel>();
  for (let userCredential of data) {
    userCredentialModel.push({
      signatureUrl: userCredential.signatureUrl
        ? process.env.VUE_APP_PeakBlobStorage.UrlCombine("peakengine", userCredential.signatureUrl)
        : "",
      userId: userCredential.userId ? userCredential.userId : 0,
    });
  }
  return userCredentialModel;
}
export function GetDocumentProductImage(data: any): Array<DocumentProductImageModel> {
  const documentProductImage = new Array<DocumentProductImageModel>();
  for (let documentProductImageItem of data) {
    documentProductImage.push({
      imageUrl: documentProductImageItem.imageUrl
        ? process.env.VUE_APP_PeakBlobStorage.UrlCombine("peakengine", documentProductImageItem.imageUrl)
        : "",
      isSelected: documentProductImageItem.isSelected ? documentProductImageItem.isSelected : false,
    });
  }
  return documentProductImage;
}
export function GetWhtCertificateModelList(data: any): Array<WhtCertificateModel> {
  const whtCertificateModelList = new Array<WhtCertificateModel>();
  data.forEach((item: any) => {
    whtCertificateModelList.push({
      whtCertificateId: item.whtCertificateId ? item.whtCertificateId : 0,
      documentContactModel: item.documentContactModel
        ? GetDocumentContact(item.documentContactModel)
        : new ContactModel(),
      whtNumber: item.whtNumber ? item.whtNumber : "",
      whtDate: item.whtDate ? item.whtDate.ToDateTimezone() : new Date(),
      subTotal: item.subTotal ? item.subTotal : 0,
      whtTotal: item.whtTotal ? item.whtTotal : 0,
      conditionType: item.conditionType ? item.conditionType : ConditionType.None,
      sourceType: item.sourceType ? item.sourceType : TaxSourceType.PeakTax,
      sourceId: item.sourceId ? item.sourceId : 0,
      itemList: item.itemList
        ? GetWitholdingTaxItemDocumentModel(item.itemList)
        : new Array<WitholdingTaxItemDocumentModel>(),
    });
  });
  return whtCertificateModelList;
}

export function GetWitholdingTaxItemDocumentModel(data: any): Array<WitholdingTaxItemDocumentModel> {
  const witholdingTaxItemDocumentModel = new Array<WitholdingTaxItemDocumentModel>();
  data.forEach((item: any) => {
    witholdingTaxItemDocumentModel.push({
      whtItemId: item.whtItemId ? item.whtItemId : 0,
      description: item.description ? item.description : "",
      taxIncomeType: item.taxIncomeType ? item.taxIncomeType : TaxIncomeType.None,
      preTaxAmount: item.preTaxAmount ? item.preTaxAmount : 0,
      whtAmount: item.whtAmount ? item.whtAmount : 0,
      whtRate: item.whtRate ? item.whtRate : 0,
    });
  });
  return witholdingTaxItemDocumentModel;
}

export function GetPP30TransactionModelList(data: any): Array<PP30TransactionModel> {
  const pp30TransactionModelList = new Array<PP30TransactionModel>();

  data.sort(
    (a: any, b: any) =>
      CompareDataListForSorting(a.taxInvoiceDate, b.taxInvoiceDate, SortingType.ASC) ||
      CompareDataListForSorting(a.taxInvoiceNumber, b.taxInvoiceNumber, SortingType.ASC)
  );

  data.forEach((item: any, index: number) => {
    pp30TransactionModelList.push({
      id: item.id ? item.id : 0,
      documentContactModel: item.documentContactModel
        ? GetDocumentContact(item.documentContactModel)
        : new ContactModel(),
      sourceType: item.sourceType ? item.sourceType : TaxSourceType.PeakTax,
      sourceId: item.sourceId ? item.sourceId : 0,
      taxInvoiceNumber: item.taxInvoiceNumber ? item.taxInvoiceNumber : "",
      taxInvoiceDate: item.taxInvoiceDate ? item.taxInvoiceDate.ToDateTimezone() : new Date(),
      taxInvoiceType: item.taxInvoiceType ? item.taxInvoiceType : 0,
      financeType: item.financeType ? item.financeType : 0,
      reference: item.reference ? item.reference : "",
      vatExemptedTotal: item.vatExemptedTotal ? item.vatExemptedTotal : 0,
      zeroVatTotal: item.zeroVatTotal ? item.zeroVatTotal : 0,
      preVatTotal: item.preVatTotal ? item.preVatTotal : 0,
      vatTotal: item.vatTotal ? item.vatTotal : 0,
      subTotal: item.subTotal ? item.subTotal : 0,
      netTotal: item.netTotal ? item.netTotal : 0,
      sequenceNumber: index + 1,
    });
  });
  return pp30TransactionModelList;
}

export function GetExecutiveSummaryDocumentData(data: any): ExecutiveSummaryDocumentModel {
  const documentDataModel: ExecutiveSummaryDocumentModel = {
    merchant: data.merchant
      ? GetMerchantExecutiveSummaryDocument(data.merchant)
      : new DocumentMerchantExecutiveSummaryModel(),
    user: data.user ? GetUserExecutiveSummaryDocument(data.user) : new DocumentUserExecutiveSummaryModel(),
    executiveSummary: data.executiveSummary
      ? GetExecutiveSummaryDocument(data.executiveSummary)
      : new DocumentExecutiveSummaryModel(),
  };

  return documentDataModel;
}

export function GetUserExecutiveSummaryDocument(_user: any) {
  const user: DocumentUserExecutiveSummaryModel = {
    name: _user.name ? _user.name : "",
    userId: _user.userId ? _user.id : 0,
  };
  return user;
}

export function GetMerchantExecutiveSummaryDocument(_merchant: any) {
  const merchant: DocumentMerchantExecutiveSummaryModel = {
    branchCode: _merchant.branchCode ? _merchant.branchCode : "",
    logoUrl: _merchant.logoUrl ? process.env.VUE_APP_PeakBlobStorage.UrlCombine("peakengine", _merchant.logoUrl) : "",
    merchantId: _merchant.merchantId ? _merchant.merchantId : 0,
    merchantType: _merchant.merchantTypes ? _merchant.merchantTypes : MerchantType.None,
    name: _merchant.name ? _merchant.name : "",
    taxNumber: _merchant.taxNumber ? _merchant.taxNumber : "",
  };
  return merchant;
}

export function GetExecutiveSummaryDocument(_executiveSummary: any) {
  const executiveSummary: DocumentExecutiveSummaryModel = {
    isDeleted: _executiveSummary.isDeleted ? _executiveSummary.isDeleted : false,
    id: _executiveSummary.id ? _executiveSummary.id : 0,
    name: _executiveSummary.name ? _executiveSummary.name : "",
    status: _executiveSummary.status ? _executiveSummary.status : ExecutiveSummaryStatus.None,
    number: _executiveSummary.number ? _executiveSummary.number : "",
    createdDateTime: _executiveSummary.createdDateTime ? _executiveSummary.createdDateTime : new Date(),
    fromDate: _executiveSummary.fromDate ? _executiveSummary.fromDate : new Date(),
    toDate: _executiveSummary.toDate ? _executiveSummary.toDate : new Date(),
    setting: _executiveSummary.setting
      ? GetSettingExecutiveSummaryDocument(_executiveSummary.setting)
      : new DocumentExecutiveSummarySettingModel(),
    transaction: _executiveSummary.transaction
      ? GetTransactionExecutiveSummaryDocument(_executiveSummary.transaction)
      : new DocumentExecutiveSummaryTransactionModel(),
  };
  return executiveSummary;
}

export function GetSettingExecutiveSummaryDocument(executiveSummarySetting: any) {
  const setting: DocumentExecutiveSummarySettingModel = new DocumentExecutiveSummarySettingModel();
  setting.operationIncomeHidden = executiveSummarySetting.operationIncomeHidden;
  setting.costsOfSalesHidden = executiveSummarySetting.costsOfSalesHidden;
  setting.grossProfitHidden = executiveSummarySetting.grossProfitHidden;
  setting.expensesHidden = executiveSummarySetting.expensesHidden;
  setting.costOfFinancingHidden = executiveSummarySetting.costOfFinancingHidden;
  setting.taxRevenuesHidden = executiveSummarySetting.taxRevenuesHidden;
  setting.netProfitHidden = executiveSummarySetting.netProfitHidden;
  setting.cashReceivedHidden = executiveSummarySetting.cashReceivedHidden;
  setting.cashSpentHidden = executiveSummarySetting.cashSpentHidden;
  setting.gainsInForeignExchangeHidden = executiveSummarySetting.gainsInForeignExchangeHidden;
  setting.netCashReceivedHidden = executiveSummarySetting.netCashReceivedHidden;
  setting.cashBalanceHidden = executiveSummarySetting.cashBalanceHidden;
  setting.accountsReceivableHidden = executiveSummarySetting.accountsReceivableHidden;
  setting.inventoryHidden = executiveSummarySetting.inventoryHidden;
  setting.accountsPayableHidden = executiveSummarySetting.accountsPayableHidden;
  setting.quickRatioHidden = executiveSummarySetting.quickRatioHidden;
  setting.inventoryTurnOverHidden = executiveSummarySetting.inventoryTurnOverHidden;
  setting.receivablesTurnOverHidden = executiveSummarySetting.receivablesTurnOverHidden;
  setting.accountPayableTurnOverRatioHidden = executiveSummarySetting.accountPayableTurnOverRatioHidden;
  setting.averagePaymentPeriodHidden = executiveSummarySetting.averagePaymentPeriodHidden;
  setting.averageInventoryPeriodHidden = executiveSummarySetting.averageInventoryPeriodHidden;
  setting.averageCollectionPeriodHidden = executiveSummarySetting.averageCollectionPeriodHidden;
  setting.cashConversionCycleHidden = executiveSummarySetting.cashConversionCycleHidden;
  setting.quotationNumberIssuedHidden = executiveSummarySetting.quotationNumberIssuedHidden;
  setting.quotationNetAmountIssuedHidden = executiveSummarySetting.quotationNetAmountIssuedHidden;
  setting.quotationAverageIssuedHidden = executiveSummarySetting.quotationAverageIssuedHidden;
  setting.invoiceNumberIssuedHidden = executiveSummarySetting.invoiceNumberIssuedHidden;
  setting.invoiceNetAmountIssuedHidden = executiveSummarySetting.invoiceNetAmountIssuedHidden;
  setting.invoiceAverageIssuedHidden = executiveSummarySetting.invoiceAverageIssuedHidden;
  setting.roeHidden = executiveSummarySetting.roeHidden;
  setting.netProfitMarginHidden = executiveSummarySetting.netProfitMarginHidden;
  setting.totalAssetTurnOverHidden = executiveSummarySetting.totalAssetTurnOverHidden;
  setting.totalEquityTurnOverHidden = executiveSummarySetting.totalEquityTurnOverHidden;
  setting.equityMultiplierHidden = executiveSummarySetting.equityMultiplierHidden;
  setting.roaHidden = executiveSummarySetting.roaHidden;
  setting.grossProfitRatioHidden = executiveSummarySetting.grossProfitRatioHidden;
  return setting;
}

export function GetTransactionExecutiveSummaryDocument(executiveSummaryTransaction: any) {
  const transaction: DocumentExecutiveSummaryTransactionModel = new DocumentExecutiveSummaryTransactionModel();
  transaction.periodList = GetExecutiveSummaryTransactionPeriodList(executiveSummaryTransaction.periodList);
  transaction.periodType = executiveSummaryTransaction.periodType
    ? executiveSummaryTransaction.periodType
    : ExecutiveSummaryPeriodType.None;
  transaction.operationIncome = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.operationIncome);
  transaction.costsOfSales = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.costsOfSales);
  transaction.grossProfit = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.grossProfit);
  transaction.expenses = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.expenses);
  transaction.costOfFinancing = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.costOfFinancing);
  transaction.taxRevenues = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.taxRevenues);
  transaction.netProfit = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.netProfit);
  transaction.cashReceived = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.cashReceived);
  transaction.cashSpent = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.cashSpent);
  transaction.gainsInForeignExchange = GetExecutiveSummaryTransactionItem(
    executiveSummaryTransaction.gainsInForeignExchange
  );
  transaction.netCashReceived = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.netCashReceived);
  transaction.cashBalance = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.cashBalance);
  transaction.accountsReceivable = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.accountsReceivable);
  transaction.inventory = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.inventory);
  transaction.accountsPayable = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.accountsPayable);
  transaction.quickRatio = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.quickRatio);
  transaction.inventoryTurnOver = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.inventoryTurnOver);
  transaction.receivablesTurnOver = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.receivablesTurnOver);
  transaction.accountPayableTurnOverRatio = GetExecutiveSummaryTransactionItem(
    executiveSummaryTransaction.accountPayableTurnOverRatio
  );
  transaction.averagePaymentPeriod = GetExecutiveSummaryTransactionItem(
    executiveSummaryTransaction.averagePaymentPeriod
  );
  transaction.averageInventoryPeriod = GetExecutiveSummaryTransactionItem(
    executiveSummaryTransaction.averageInventoryPeriod
  );
  transaction.averageCollectionPeriod = GetExecutiveSummaryTransactionItem(
    executiveSummaryTransaction.averageCollectionPeriod
  );
  transaction.cashConversionCycle = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.cashConversionCycle);
  transaction.quotationNumberIssued = GetExecutiveSummaryTransactionItem(
    executiveSummaryTransaction.quotationNumberIssued
  );
  transaction.quotationNetAmountIssued = GetExecutiveSummaryTransactionItem(
    executiveSummaryTransaction.quotationNetAmountIssued
  );
  transaction.quotationAverageIssued = GetExecutiveSummaryTransactionItem(
    executiveSummaryTransaction.quotationAverageIssued
  );
  transaction.invoiceNumberIssued = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.invoiceNumberIssued);
  transaction.invoiceNetAmountIssued = GetExecutiveSummaryTransactionItem(
    executiveSummaryTransaction.invoiceNetAmountIssued
  );
  transaction.invoiceAverageIssued = GetExecutiveSummaryTransactionItem(
    executiveSummaryTransaction.invoiceAverageIssued
  );
  transaction.roe = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.roe);
  transaction.netProfitMargin = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.netProfitMargin);
  transaction.totalAssetTurnOver = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.totalAssetTurnOver);
  transaction.equityMultiplier = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.equityMultiplier);
  transaction.roa = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.roa);
  transaction.totalEquityTurnOver = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.totalEquityTurnOver);
  transaction.grossProfitRatio = GetExecutiveSummaryTransactionItem(executiveSummaryTransaction.grossProfitRatio);
  return transaction;
}

export function GetExecutiveSummaryTransactionPeriodList(executiveSummaryTransactionPeriodList: any) {
  const periodList: Array<DocumentExecutiveSummaryPeriodModel> = new Array<DocumentExecutiveSummaryPeriodModel>();
  if (executiveSummaryTransactionPeriodList && executiveSummaryTransactionPeriodList.length > 0) {
    for (let executiveSummaryTransactionPeriod of executiveSummaryTransactionPeriodList) {
      const period: DocumentExecutiveSummaryPeriodModel = {
        fromDate: executiveSummaryTransactionPeriod.fromDate,
        toDate: executiveSummaryTransactionPeriod.toDate,
      };
      periodList.push(period);
    }
  }
  return periodList;
}

export function GetExecutiveSummaryTransactionItem(executiveSummaryTransactionItem: any) {
  const transactionItem: DocumentExecutiveSummaryTransactionItemModel =
    new DocumentExecutiveSummaryTransactionItemModel();
  if (executiveSummaryTransactionItem) {
    transactionItem.remark = GetExecutiveSummaryTransactionItemRemark(executiveSummaryTransactionItem.remark);
    transactionItem.type = executiveSummaryTransactionItem.type;
    transactionItem.accountList = GetExecutiveSummaryTransactionItemAccountList(
      executiveSummaryTransactionItem.accountList
    );
    transactionItem.summaryList = GetExecutiveSummaryValueList(executiveSummaryTransactionItem.summaryList);
  }

  return transactionItem;
}

export function GetExecutiveSummaryTransactionItemRemark(executiveSummaryTransactionItemRemark: any) {
  const remark: DocumentExecutiveSummaryRemarkModel = {
    description: executiveSummaryTransactionItemRemark.description,
    header: executiveSummaryTransactionItemRemark.header,
    no: executiveSummaryTransactionItemRemark.no,
  };
  return remark;
}

export function GetExecutiveSummaryTransactionItemAccountList(executiveSummaryTransactionItemAccountList: any) {
  const accountList: Array<DocumentExecutiveSummaryAccountModel> = new Array<DocumentExecutiveSummaryAccountModel>();
  if (executiveSummaryTransactionItemAccountList && executiveSummaryTransactionItemAccountList.length > 0) {
    for (let executiveSummaryTransactionItemAccount of executiveSummaryTransactionItemAccountList) {
      const account: DocumentExecutiveSummaryAccountModel = {
        accountId: executiveSummaryTransactionItemAccount.accountId,
        valueList: GetExecutiveSummaryValueList(executiveSummaryTransactionItemAccount.valueList),
      };
      accountList.push(account);
    }
  }
  return accountList;
}

export function GetExecutiveSummaryValueList(executiveSummaryTransactionItemValueList: any) {
  const valueList: Array<DocumentExecutiveSummaryValueModel> = new Array<DocumentExecutiveSummaryValueModel>();
  if (executiveSummaryTransactionItemValueList && executiveSummaryTransactionItemValueList.length > 0) {
    for (let executiveSummaryTransactionItemValue of executiveSummaryTransactionItemValueList) {
      const value: DocumentExecutiveSummaryValueModel = {
        changePercent: executiveSummaryTransactionItemValue.changePercent,
        value: executiveSummaryTransactionItemValue.value,
      };
      valueList.push(value);
    }
  }
  return valueList;
}

export function GetDocumentDepositReferenceItemModelsModel(DepositReferenceItemModelList: Array<any>) {
  const depositRefList: Array<DepositReferenceItemModel> = new Array<DepositReferenceItemModel>();
  if (DepositReferenceItemModelList && DepositReferenceItemModelList.length > 0) {
    for (const depositItem of DepositReferenceItemModelList) {
      const deposit: DepositReferenceItemModel = {
        currency: GetDocumentCurrency(depositItem.currency),
        summary: GetDocumentSummary(depositItem.summary),
        transactionDate: depositItem.transactionDate,
        transactionNumber: depositItem.transactionNumber,
        transactionId: depositItem.transactionId,
        transactionType: depositItem.transactionType,
      };
      depositRefList.push(deposit);
    }
  }
  return depositRefList;
}

export function GetClassificationsItemsModel(classificationList: Array<any>) {
  const classificationRefList: Array<ClassificationsItemModel> = new Array<ClassificationsItemModel>();
  if (classificationList && classificationList.length > 0) {
    for (const classificationItem of classificationList) {
      const classification: ClassificationsItemModel = {
        classificationId: classificationItem.classificationId,
        classificationItems: GetClassificationItem(classificationItem.classificationItems),
        classificationNumber: classificationItem.classificationNumber,
        name: classificationItem.name,
      };
      classificationRefList.push(classification);
    }
  }
  return classificationRefList;
}

function GetClassificationItem(classificationItemsList: Array<any>) {
  const itemList: Array<ClassificationItemElement> = new Array<ClassificationItemElement>();
  for (const classificationItem of classificationItemsList) {
    const item: ClassificationItemElement = {
      classificationItemId: classificationItem.classificationItemId,
      name: classificationItem.name,
      classificationItemNumber: classificationItem.classificationItemNumber,
    };
    itemList.push(item);
  }
  return itemList;
}
